// ============================================
// SEAFARER DETAIL PAGE - i18n
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit,
  User,
  MapPin,
  Award,
  Calendar,
  Phone,
  Mail,
  Ship,
  FileText,
  ShieldCheck,
  Flag,
  Briefcase,
  Anchor,
  Loader2,
  ShieldOff,
} from 'lucide-react';
import { useTranslation } from '@crew-at-sea/i18n';
import { PageHeader, VerificationBadge } from '@/components';
import { adminApi } from '@/services/api';
import { formatDate, getInitials, cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/seafarers/$id/')({
  component: SeafarerDetailPage,
});

function SeafarerDetailPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const queryClient = useQueryClient();

  const { data: seafarerData, isLoading } = useQuery({
    queryKey: ['admin', 'seafarers', id],
    queryFn: () => adminApi.getSeafarer(id),
  });

  const seafarer = (seafarerData as any)?.data || seafarerData;

  // Doğrulama toggle mutation
  const verifyMutation = useMutation({
    mutationFn: (isVerified: boolean) => adminApi.verifySeafarer(id, isVerified),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'seafarers', id] });
    },
  });

  const departmentColors: Record<string, string> = {
    DECK: 'bg-blue-100 text-blue-800',
    ENGINE: 'bg-green-100 text-green-800',
    ELECTRICAL: 'bg-amber-100 text-amber-800',
    CATERING: 'bg-purple-100 text-purple-800',
    OTHER: 'bg-gray-100 text-gray-800',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
      </div>
    );
  }

  if (!seafarer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('panel:seafarers.notFound')}</p>
        <Link to="/seafarers" className="text-maritime-600 hover:underline mt-2 inline-block">
          {t('panel:seafarers.backToSeafarers')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:seafarers.detail')}
        description={`${seafarer.firstName} ${seafarer.lastName}`}
        actions={
          <div className="flex gap-2">
            <Link
              to="/seafarers"
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-maritime hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common:back')}
            </Link>
            <Link
              to={`/seafarers/${id}/edit` as any}
              className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700"
            >
              <Edit className="h-4 w-4" />
              {t('common:edit')}
            </Link>
          </div>
        }
      />

      {/* Profile Header */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-ocean-100 text-ocean-700 font-bold text-2xl">
            {seafarer.avatarUrl ? (
              <img
                src={seafarer.avatarUrl}
                alt={`${seafarer.firstName} ${seafarer.lastName}`}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              getInitials(seafarer.firstName, seafarer.lastName)
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-semibold">
                {seafarer.firstName} {seafarer.lastName}
              </h2>
              <VerificationBadge isVerified={seafarer.isVerified} />
            </div>
            <p className="text-gray-600">
              {seafarer.currentRank?.nameTr || seafarer.currentRank?.nameEn || t('panel:seafarers.noPosition')}
            </p>
            <span
              className={cn(
                'inline-block mt-2 px-2.5 py-0.5 text-xs font-medium rounded-full',
                departmentColors[seafarer.department] || departmentColors.OTHER
              )}
            >
              {t(`enums:department.${seafarer.department}`, { defaultValue: seafarer.department })}
            </span>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">{t('panel:seafarers.profileCompletion')}</div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full',
                    seafarer.profileCompleteness >= 80
                      ? 'bg-green-500'
                      : seafarer.profileCompleteness >= 50
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                  )}
                  style={{ width: `${seafarer.profileCompleteness || 0}%` }}
                />
              </div>
              <span className="text-sm font-medium">{seafarer.profileCompleteness || 0}%</span>
            </div>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 border-b pb-2">{t('panel:seafarers.contactInfo')}</h3>

            {seafarer.user?.email && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-maritime-100 rounded-full">
                  <Mail className="h-4 w-4 text-maritime-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('common:email')}</p>
                  <p className="font-medium text-sm">{seafarer.user.email}</p>
                </div>
              </div>
            )}

            {seafarer.phone && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-maritime-100 rounded-full">
                  <Phone className="h-4 w-4 text-maritime-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('common:phone')}</p>
                  <p className="font-medium text-sm">{seafarer.phone}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <MapPin className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.location')}</p>
                <p className="font-medium text-sm">
                  {seafarer.city ? `${seafarer.city}, ` : ''}
                  {seafarer.country || seafarer.nationality}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Flag className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.nationality')}</p>
                <p className="font-medium text-sm">{seafarer.nationality}</p>
              </div>
            </div>
          </div>

          {/* Experience Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 border-b pb-2">{t('panel:seafarers.experienceInfo')}</h3>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Briefcase className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.totalExperience')}</p>
                <p className="font-medium text-sm">{t('panel:seafarers.yearsExp', { count: seafarer.experienceYears || 0 })}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Anchor className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.totalSeaDays')}</p>
                <p className="font-medium text-sm">
                  {(seafarer.totalSeaDays || 0).toLocaleString('tr-TR')} {t('panel:seafarers.days')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Ship className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.preferredVesselType')}</p>
                <p className="font-medium text-sm">
                  {seafarer.preferredVesselType
                    ? t(`enums:vesselType.${seafarer.preferredVesselType}`, { defaultValue: seafarer.preferredVesselType })
                    : t('panel:seafarers.notSpecified')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Award className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.jobStatus')}</p>
                <p className="font-medium text-sm">
                  {seafarer.availableForHire ? (
                    <span className="text-green-600">{t('panel:seafarers.availableForHire')}</span>
                  ) : (
                    <span className="text-gray-500">{t('panel:seafarers.employed')}</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Personal Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 border-b pb-2">{t('panel:seafarers.personalInfo')}</h3>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <User className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.gender')}</p>
                <p className="font-medium text-sm">
                  {seafarer.gender
                    ? t(`enums:gender.${seafarer.gender}`, { defaultValue: seafarer.gender })
                    : t('panel:seafarers.notSpecified')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Calendar className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.dateOfBirth')}</p>
                <p className="font-medium text-sm">
                  {seafarer.dateOfBirth ? formatDate(seafarer.dateOfBirth) : t('panel:seafarers.notSpecified')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Calendar className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.registeredAt')}</p>
                <p className="font-medium text-sm">{formatDate(seafarer.createdAt)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <ShieldCheck className="h-4 w-4 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('panel:seafarers.accountStatus')}</p>
                <p className="font-medium text-sm">
                  {seafarer.user?.status
                    ? t(`enums:userStatus.${seafarer.user.status}`, { defaultValue: seafarer.user.status })
                    : t('panel:seafarers.unknown')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Certificates Section */}
      {seafarer.certificates && seafarer.certificates.length > 0 && (
        <div className="bg-white rounded-maritime shadow-maritime p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-maritime-600" />
            {t('panel:seafarers.certificates')} ({seafarer.certificates.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {seafarer.certificates.map((cert: any) => (
              <div key={cert.id} className="border rounded-lg p-4">
                <div className="font-medium text-sm">
                  {cert.certificateType?.nameTr || cert.certificateType?.nameEn || t('panel:seafarers.certificate')}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {t('panel:seafarers.certNumber')}: {cert.certificateNumber || '-'}
                </div>
                <div className="text-xs text-gray-500">
                  {t('panel:seafarers.issueDate')}: {cert.issueDate ? formatDate(cert.issueDate) : '-'}
                </div>
                {cert.expiryDate && (
                  <div className={cn(
                    'text-xs mt-1',
                    new Date(cert.expiryDate) < new Date() ? 'text-red-600' : 'text-gray-500'
                  )}>
                    {t('panel:seafarers.expiryDate')}: {formatDate(cert.expiryDate)}
                    {new Date(cert.expiryDate) < new Date() && ` (${t('panel:seafarers.expired')})`}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sea Service Records */}
      {seafarer.seaServiceRecords && seafarer.seaServiceRecords.length > 0 && (
        <div className="bg-white rounded-maritime shadow-maritime p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Ship className="h-5 w-5 text-maritime-600" />
            {t('panel:seafarers.seaServiceRecords')} ({seafarer.seaServiceRecords.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">{t('panel:seafarers.vesselName')}</th>
                  <th className="text-left py-2 px-3">{t('panel:seafarers.vesselType')}</th>
                  <th className="text-left py-2 px-3">{t('panel:seafarers.position')}</th>
                  <th className="text-left py-2 px-3">{t('panel:seafarers.startDate')}</th>
                  <th className="text-left py-2 px-3">{t('panel:seafarers.endDate')}</th>
                  <th className="text-left py-2 px-3">{t('panel:seafarers.daysColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {seafarer.seaServiceRecords.map((record: any) => (
                  <tr key={record.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{record.vesselName}</td>
                    <td className="py-2 px-3">{record.vesselType}</td>
                    <td className="py-2 px-3">{record.rank?.nameTr || record.position || '-'}</td>
                    <td className="py-2 px-3">{formatDate(record.startDate)}</td>
                    <td className="py-2 px-3">
                      {record.endDate ? formatDate(record.endDate) : t('panel:seafarers.ongoing')}
                    </td>
                    <td className="py-2 px-3">{record.totalDays || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Applications Section */}
      {seafarer.applications && seafarer.applications.length > 0 && (
        <div className="bg-white rounded-maritime shadow-maritime p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-maritime-600" />
            {t('panel:seafarers.applications')} ({seafarer.applications.length})
          </h3>
          <div className="space-y-3">
            {seafarer.applications.slice(0, 5).map((app: any) => (
              <div key={app.id} className="flex items-center justify-between border-b pb-3">
                <div>
                  <div className="font-medium text-sm">{app.job?.title || t('panel:seafarers.job')}</div>
                  <div className="text-xs text-gray-500">{app.job?.company?.name || '-'}</div>
                </div>
                <div className="text-right">
                  <span className={cn(
                    'px-2 py-1 text-xs rounded-full',
                    app.status === 'PENDING' && 'bg-yellow-100 text-yellow-800',
                    app.status === 'REVIEWED' && 'bg-blue-100 text-blue-800',
                    app.status === 'SHORTLISTED' && 'bg-purple-100 text-purple-800',
                    app.status === 'ACCEPTED' && 'bg-green-100 text-green-800',
                    app.status === 'REJECTED' && 'bg-red-100 text-red-800',
                    app.status === 'WITHDRAWN' && 'bg-gray-100 text-gray-800'
                  )}>
                    {t(`enums:applicationStatus.${app.status}`, { defaultValue: app.status })}
                  </span>
                  <div className="text-xs text-gray-400 mt-1">{formatDate(app.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Aksiyonları */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Admin Aksiyonları</h3>
        <div className="flex items-center gap-4">
          <button
            onClick={() => verifyMutation.mutate(!seafarer.isVerified)}
            disabled={verifyMutation.isPending}
            className={`flex items-center gap-2 px-4 py-2 rounded-maritime text-sm font-medium transition-colors disabled:opacity-50 ${
              seafarer.isVerified
                ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
            }`}
          >
            {verifyMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : seafarer.isVerified ? (
              <ShieldOff className="h-4 w-4" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            {seafarer.isVerified ? 'Doğrulamayı Kaldır' : 'Doğrula'}
          </button>
        </div>
      </div>
    </div>
  );
}
